import { Link, useParams, Navigate } from 'react-router-dom';
import Seo, { SITE_URL } from '../components/Seo';
import JsonLd from '../components/JsonLd';
import { getPostBySlug } from '../data/blogPosts';
import { BLOG_ARTICLES } from '../content/blogArticles';
import { getToolById } from '../data/tools';
import { getToolBanner } from '../data/toolBanners';

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const article = BLOG_ARTICLES[slug];

  if (!post || !article) {
    return <Navigate to="/blog" replace />;
  }

  const tool = getToolById(post.relatedTool);
  const banner = tool ? getToolBanner(tool.id) : null;
  const articleUrl = `${SITE_URL}/blog/${slug}`;

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BlogPosting',
        '@id': `${articleUrl}#article`,
        headline: post.title,
        description: post.excerpt,
        datePublished: post.date,
        dateModified: post.date,
        inLanguage: 'en',
        mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
        author: { '@type': 'Organization', name: 'PixelUtility', url: SITE_URL },
        publisher: {
          '@type': 'Organization',
          name: 'PixelUtility',
          url: SITE_URL,
          logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
        },
        ...(banner && { image: banner.ogImage }),
        articleSection: post.category,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
          { '@type': 'ListItem', position: 3, name: post.title, item: articleUrl },
        ],
      },
    ],
  };

  return (
    <>
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/blog/${slug}`}
        image={banner?.ogImage}
        imageAlt={banner?.alt}
        imageWidth={banner?.width}
        imageHeight={banner?.height}
      />
      <JsonLd data={schema} />
      <article className="pt-24 pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          <nav className="text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-brand-600">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/blog" className="hover:text-brand-600">Blog</Link>
          </nav>
          <span className="text-xs font-medium text-brand-600 bg-brand-50 px-2 py-0.5 rounded-full">
            {post.category}
          </span>
          <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold text-gray-900">{post.title}</h1>
          <time className="mt-4 block text-sm text-gray-400" dateTime={post.date}>
            {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </time>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">{post.excerpt}</p>

          {article.sections.map((section) => (
            <section key={section.title} className="mt-10">
              <h2 className="text-xl font-bold text-gray-900">{section.title}</h2>
              {section.paragraphs.map((p, i) => (
                <p key={i} className="mt-4 text-gray-600 leading-relaxed">
                  {p}
                </p>
              ))}
            </section>
          ))}

          {tool && (
            <div className="mt-12 p-6 rounded-2xl bg-brand-50 border border-brand-100">
              <p className="font-semibold text-gray-900">Try the free tool</p>
              <p className="mt-2 text-sm text-gray-600">{tool.description}</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link to={tool.landingPath} className="text-sm font-medium text-brand-600 hover:underline">
                  Read full {tool.shortName} guide
                </Link>
                <a
                  href={tool.launchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-brand-600 hover:underline"
                >
                  Launch tool →
                </a>
              </div>
            </div>
          )}
        </div>
      </article>
    </>
  );
}
